import Decimal from 'decimal.js';

import type {
  CalendarYearReturn,
  InvestmentAccountReturns,
  InvestmentCashCategory,
  InvestmentCurrencyReturns,
  InvestmentHoldingReturn,
  InvestmentPeriodReturn,
  InvestmentReturnPeriod,
  InvestmentReturnsBreakdown,
  InvestmentReturnsIssue,
} from '../schemas/investmentReturns';

export interface ReturnsCashEvent {
  date: string;
  amount: string;
  category: InvestmentCashCategory;
  symbol: string | null;
  label: string;
}

export interface ReturnsTrade {
  date: string;
  symbol: string;
  side: 'BUY' | 'SELL';
  quantity: string;
  netCash: string;
}

export interface ReturnsSnapshot {
  date: string;
  totalValue: string;
  cash: string;
}

export interface ReturnsHolding {
  holdingId: string;
  symbol: string;
  units: string;
  value: string;
  cost: string;
}

export interface AccountReturnsInput {
  accountId: string;
  accountName: string;
  currency: string;
  valuationAvailable: boolean;
  snapshots: readonly ReturnsSnapshot[];
  holdings: readonly ReturnsHolding[];
  trades: readonly ReturnsTrade[];
  cashEvents: readonly ReturnsCashEvent[];
}

export interface DatedAmount {
  date: string;
  amount: Decimal;
}

export interface SeriesPoint {
  date: string;
  value: Decimal;
}

export interface AccountReturnsResult {
  account: InvestmentAccountReturns;
  holdings: ReadonlyMap<string, InvestmentHoldingReturn>;
  /** Investor-perspective flows for XIRR: money paid in is negative, including any opening cash. */
  investorFlows: readonly DatedAmount[];
  /** Account-perspective contributions (+) and withdrawals (-) used for period returns. */
  externalFlows: readonly DatedAmount[];
  series: readonly SeriesPoint[];
}

const CASH_TOLERANCE = new Decimal(1);
const UNITS_TOLERANCE = new Decimal('0.000001');
const PERIOD_START_TOLERANCE_DAYS = 7;
const CORPORATE_ACTION_MATCH_DAYS = 7;
const DAY_MS = 86_400_000;
const ZERO = new Decimal(0);

function dayNumber(date: string): number {
  return Math.floor(Date.parse(`${date}T00:00:00Z`) / DAY_MS);
}

function isoDay(day: number): string {
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

function addMonths(date: string, months: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  const target = new Date(Date.UTC(year, month - 1 + months, 1));
  const lastDay = new Date(
    Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0),
  ).getUTCDate();
  target.setUTCDate(Math.min(day, lastDay));
  return target.toISOString().slice(0, 10);
}

function sum(values: readonly Decimal[]): Decimal {
  return values.reduce((total, value) => total.plus(value), ZERO);
}

function pct(value: Decimal | null): string | null {
  return value === null ? null : value.times(100).toString();
}

function money(amount: Decimal): string {
  return amount.toDecimalPlaces(4).toString();
}

function npv(flows: readonly DatedAmount[], rate: Decimal, origin: number): Decimal {
  const base = rate.plus(1);
  return sum(flows.map((flow) => flow.amount.div(base.pow((dayNumber(flow.date) - origin) / 365))));
}

/** Annualised money-weighted return; null when it does not exist or cannot be bracketed. */
export function xirr(flows: readonly DatedAmount[]): Decimal | null {
  const meaningful = flows.filter((flow) => !flow.amount.isZero());
  if (
    !meaningful.some((flow) => flow.amount.gt(0)) ||
    !meaningful.some((flow) => flow.amount.lt(0))
  )
    return null;
  const origin = Math.min(...meaningful.map((flow) => dayNumber(flow.date)));
  if (meaningful.every((flow) => dayNumber(flow.date) === origin)) return null;
  let low = new Decimal('-0.9999');
  let high = new Decimal(10);
  let lowValue = npv(meaningful, low, origin);
  let highValue = npv(meaningful, high, origin);
  while (lowValue.isPositive() === highValue.isPositive() && high.lt(10_000)) {
    high = high.times(10);
    highValue = npv(meaningful, high, origin);
  }
  if (lowValue.isPositive() === highValue.isPositive()) return null;
  for (let iteration = 0; iteration < 200; iteration += 1) {
    const mid = low.plus(high).div(2);
    const midValue = npv(meaningful, mid, origin);
    if (midValue.isZero() || high.minus(low).abs().lt('1e-12')) return mid;
    if (midValue.isPositive() === lowValue.isPositive()) {
      low = mid;
      lowValue = midValue;
    } else {
      high = mid;
    }
  }
  return low.plus(high).div(2);
}

/** Modified Dietz return for one sub-period; flows are account-perspective (+ in, - out). */
export function modifiedDietz(input: {
  startValue: Decimal;
  endValue: Decimal;
  startDate: string;
  endDate: string;
  flows: readonly DatedAmount[];
}): Decimal | null {
  const length = dayNumber(input.endDate) - dayNumber(input.startDate);
  if (length <= 0) return null;
  const net = sum(input.flows.map((flow) => flow.amount));
  const weighted = sum(
    input.flows.map((flow) =>
      flow.amount.times(dayNumber(input.endDate) - dayNumber(flow.date)).div(length),
    ),
  );
  const denominator = input.startValue.plus(weighted);
  if (denominator.lte(0)) return null;
  return input.endValue.minus(input.startValue).minus(net).div(denominator);
}

function flowsBetween(flows: readonly DatedAmount[], startDate: string, endDate: string) {
  return flows.filter((flow) => flow.date > startDate && flow.date <= endDate);
}

function periodTarget(period: InvestmentReturnPeriod, endDate: string, first: string): string {
  switch (period) {
    case '1M':
      return addMonths(endDate, -1);
    case '3M':
      return addMonths(endDate, -3);
    case '1Y':
      return addMonths(endDate, -12);
    case 'YTD':
      return `${endDate.slice(0, 4)}-01-01`;
    case 'SINCE_TRACKING':
      return first;
  }
}

function startPoint(
  series: readonly SeriesPoint[],
  period: InvestmentReturnPeriod,
  target: string,
): SeriesPoint | null {
  if (period === 'SINCE_TRACKING') return series[0] ?? null;
  const earliest = isoDay(dayNumber(target) - PERIOD_START_TOLERANCE_DAYS);
  const candidates = series.filter((point) => point.date <= target && point.date >= earliest);
  return candidates.at(-1) ?? null;
}

function unavailablePeriod(period: InvestmentReturnPeriod, reason: string): InvestmentPeriodReturn {
  return {
    period,
    returnPct: null,
    gain: null,
    netFlows: null,
    startDate: null,
    endDate: null,
    unavailableReason: reason,
  };
}

const PERIODS: readonly InvestmentReturnPeriod[] = ['1M', '3M', 'YTD', '1Y', 'SINCE_TRACKING'];

type ChainedGrowth = { growth: Decimal } | { failedFrom: string; failedTo: string };

/** Time-weighted growth factor: Modified Dietz per valuation interval, geometrically linked. */
function chainedGrowth(
  points: readonly SeriesPoint[],
  flows: readonly DatedAmount[],
): ChainedGrowth {
  let growth = new Decimal(1);
  for (let index = 1; index < points.length; index += 1) {
    const previous = points[index - 1];
    const current = points[index];
    if (!previous || !current) continue;
    const step = modifiedDietz({
      startValue: previous.value,
      endValue: current.value,
      startDate: previous.date,
      endDate: current.date,
      flows: flowsBetween(flows, previous.date, current.date),
    });
    if (step === null) return { failedFrom: previous.date, failedTo: current.date };
    growth = growth.times(step.plus(1));
  }
  return { growth };
}

function boundaryPoint(series: readonly SeriesPoint[], target: string): SeriesPoint | null {
  const earliest = isoDay(dayNumber(target) - PERIOD_START_TOLERANCE_DAYS);
  return series.filter((point) => point.date <= target && point.date >= earliest).at(-1) ?? null;
}

function unavailableYear(year: number, yearToDate: boolean, reason: string): CalendarYearReturn {
  return {
    year,
    returnPct: null,
    gain: null,
    netFlows: null,
    startDate: null,
    endDate: null,
    partial: false,
    yearToDate,
    unavailableReason: reason,
  };
}

/**
 * Calendar-year time-weighted returns, newest year first. A year needs a valuation within a week
 * of 1 January (or tracking starting during that year, reported as partial) and, unless it is the
 * current year, a valuation within a week before the following 1 January.
 * Flows are account-perspective: contributions positive, withdrawals negative.
 */
export function calendarYearReturns(
  series: readonly SeriesPoint[],
  flows: readonly DatedAmount[],
): CalendarYearReturn[] {
  const first = series[0];
  const last = series.at(-1);
  if (!first || !last) return [];
  const firstYear = Number(first.date.slice(0, 4));
  const lastYear = Number(last.date.slice(0, 4));
  const years: CalendarYearReturn[] = [];
  for (let year = lastYear; year >= firstYear; year -= 1) {
    const yearToDate = year === lastYear;
    const yearStart = `${year}-01-01`;
    const nextYearStart = `${year + 1}-01-01`;
    const boundaryStart = boundaryPoint(series, yearStart);
    const start = boundaryStart ?? (first.date > yearStart ? first : null);
    const end = yearToDate ? last : boundaryPoint(series, nextYearStart);
    if (!start) {
      years.push(unavailableYear(year, yearToDate, `Needs a valuation near ${yearStart}.`));
      continue;
    }
    if (!end) {
      years.push(
        unavailableYear(
          year,
          yearToDate,
          `Needs a valuation dated between ${isoDay(dayNumber(nextYearStart) - PERIOD_START_TOLERANCE_DAYS)} and ${nextYearStart}.`,
        ),
      );
      continue;
    }
    if (start.date >= end.date) {
      years.push(
        unavailableYear(
          year,
          yearToDate,
          `Tracking began ${first.date}; not enough valuations yet.`,
        ),
      );
      continue;
    }
    const points = series.filter((point) => point.date >= start.date && point.date <= end.date);
    const chained = chainedGrowth(points, flows);
    if ('failedFrom' in chained) {
      years.push(
        unavailableYear(
          year,
          yearToDate,
          `Withdrawals between ${chained.failedFrom} and ${chained.failedTo} exceed the starting value.`,
        ),
      );
      continue;
    }
    const netFlows = sum(flowsBetween(flows, start.date, end.date).map((flow) => flow.amount));
    years.push({
      year,
      returnPct: pct(chained.growth.minus(1)),
      gain: money(end.value.minus(start.value).minus(netFlows)),
      netFlows: money(netFlows),
      startDate: start.date,
      endDate: end.date,
      partial: boundaryStart === null,
      yearToDate,
      unavailableReason: null,
    });
  }
  return years;
}

function accountPeriods(
  series: readonly SeriesPoint[],
  flows: readonly DatedAmount[],
): InvestmentPeriodReturn[] {
  const first = series[0];
  const end = series.at(-1);
  if (!first || !end || series.length < 2) {
    const reason = first
      ? `Tracking began ${first.date}. Import another holdings snapshot later to measure this period.`
      : 'No holdings snapshot has been imported.';
    return PERIODS.map((period) => unavailablePeriod(period, reason));
  }
  return PERIODS.map((period) => {
    const target = periodTarget(period, end.date, first.date);
    const start = startPoint(series, period, target);
    if (!start || start.date >= end.date) {
      return unavailablePeriod(
        period,
        `Needs a holdings snapshot dated between ${isoDay(dayNumber(target) - PERIOD_START_TOLERANCE_DAYS)} and ${target}. Tracking began ${first.date}.`,
      );
    }
    const points = series.filter((point) => point.date >= start.date && point.date <= end.date);
    const chained = chainedGrowth(points, flows);
    if ('failedFrom' in chained) {
      return unavailablePeriod(
        period,
        `Withdrawals between ${chained.failedFrom} and ${chained.failedTo} exceed the starting value.`,
      );
    }
    const growth = chained.growth;
    const netFlows = sum(flowsBetween(flows, start.date, end.date).map((flow) => flow.amount));
    return {
      period,
      returnPct: pct(growth.minus(1)),
      gain: money(end.value.minus(start.value).minus(netFlows)),
      netFlows: money(netFlows),
      startDate: start.date,
      endDate: end.date,
      unavailableReason: null,
    };
  });
}

function emptyBreakdown(): InvestmentReturnsBreakdown {
  return {
    contributions: '0',
    withdrawals: '0',
    openingCash: null,
    dividends: '0',
    incomeTransfers: '0',
    interest: '0',
    fees: '0',
    corporateActions: '0',
    otherIncome: '0',
    unclassified: '0',
    closedPositionsRealised: '0',
    closedPositionsCount: 0,
    closedPositionsExcludedCount: 0,
  };
}

function buildSeries(snapshots: readonly ReturnsSnapshot[]): SeriesPoint[] {
  const byDate = new Map<string, Decimal>();
  for (const snapshot of [...snapshots].sort((left, right) => left.date.localeCompare(right.date)))
    byDate.set(snapshot.date, new Decimal(snapshot.totalValue));
  return [...byDate].map(([date, value]) => ({ date, value }));
}

function isMatchedCorporateAction(event: ReturnsCashEvent, trades: readonly ReturnsTrade[]) {
  return (
    event.category === 'CORPORATE_ACTION' &&
    trades.some(
      (trade) =>
        trade.side === 'SELL' &&
        new Decimal(trade.netCash).equals(event.amount) &&
        Math.abs(dayNumber(trade.date) - dayNumber(event.date)) <= CORPORATE_ACTION_MATCH_DAYS,
    )
  );
}

function formatUnits(units: Decimal): string {
  return `${units.gt(0) ? '+' : ''}${units.toString()} units`;
}

export function computeAccountReturns(input: AccountReturnsInput): AccountReturnsResult {
  const series = buildSeries(input.snapshots);
  const latest = series.at(-1);
  const latestSnapshot = [...input.snapshots]
    .sort((left, right) => left.date.localeCompare(right.date))
    .at(-1);
  const base = {
    accountId: input.accountId,
    accountName: input.accountName,
    currency: input.currency,
    trackingSince: series[0]?.date ?? null,
    snapshotCount: series.length,
  };
  if (!input.valuationAvailable || !latest || !latestSnapshot) {
    return {
      account: {
        ...base,
        status: 'UNAVAILABLE',
        historyStart: null,
        valuationDate: null,
        currentValue: null,
        netContributions: null,
        gain: null,
        gainPct: null,
        annualisedReturnPct: null,
        breakdown: emptyBreakdown(),
        issues: [
          {
            kind: 'VALUATION_UNAVAILABLE',
            message: 'No usable holdings valuation, so returns cannot be calculated.',
            amount: null,
            items: [],
          },
        ],
        periods: PERIODS.map((period) =>
          unavailablePeriod(period, 'No usable holdings valuation.'),
        ),
        calendarYears: [],
        categories: [],
      },
      holdings: new Map(),
      investorFlows: [],
      externalFlows: [],
      series,
    };
  }

  const valuationDate = latest.date;
  const trades = input.trades.filter((trade) => trade.date <= valuationDate);
  const events = input.cashEvents
    .filter((event) => event.date <= valuationDate)
    .filter((event) => !isMatchedCorporateAction(event, trades));
  const externalFlows = events
    .filter((event) => event.category === 'CONTRIBUTION' || event.category === 'WITHDRAWAL')
    .map((event) => ({ date: event.date, amount: new Decimal(event.amount) }));
  const periods = accountPeriods(series, externalFlows);
  const calendarYears = calendarYearReturns(series, externalFlows);

  const byCategory = (category: InvestmentCashCategory) =>
    sum(
      events
        .filter((event) => event.category === category)
        .map((event) => new Decimal(event.amount)),
    );
  const categories = [...new Set(events.map((event) => event.category))].sort().map((category) => {
    const rows = events.filter((event) => event.category === category);
    return {
      category,
      count: rows.length,
      total: money(sum(rows.map((row) => new Decimal(row.amount)))),
      examples: [...new Set(rows.map((row) => row.label))].slice(0, 3),
    };
  });

  const currentUnits = new Map<string, Decimal>();
  for (const holding of input.holdings)
    currentUnits.set(
      holding.symbol,
      (currentUnits.get(holding.symbol) ?? ZERO).plus(holding.units),
    );
  const openingUnits = new Map(currentUnits);
  const symbolTrades = new Map<string, { invested: Decimal; proceeds: Decimal }>();
  for (const trade of trades) {
    const quantity = new Decimal(trade.quantity);
    const units = openingUnits.get(trade.symbol) ?? ZERO;
    openingUnits.set(
      trade.symbol,
      trade.side === 'BUY' ? units.minus(quantity) : units.plus(quantity),
    );
    const totals = symbolTrades.get(trade.symbol) ?? { invested: ZERO, proceeds: ZERO };
    const cash = new Decimal(trade.netCash);
    symbolTrades.set(
      trade.symbol,
      trade.side === 'BUY'
        ? { ...totals, invested: totals.invested.plus(cash.abs()) }
        : { ...totals, proceeds: totals.proceeds.plus(cash) },
    );
  }
  const incomeBySymbol = new Map<string, Decimal>();
  for (const event of events) {
    if (event.category !== 'DIVIDEND' || !event.symbol) continue;
    incomeBySymbol.set(event.symbol, (incomeBySymbol.get(event.symbol) ?? ZERO).plus(event.amount));
  }
  const reconciled = (symbol: string) =>
    (openingUnits.get(symbol) ?? ZERO).abs().lte(UNITS_TOLERANCE);

  const holdings = new Map<string, InvestmentHoldingReturn>();
  for (const holding of input.holdings) {
    const totals = symbolTrades.get(holding.symbol) ?? { invested: ZERO, proceeds: ZERO };
    const income = incomeBySymbol.get(holding.symbol) ?? ZERO;
    if (!reconciled(holding.symbol)) {
      holdings.set(holding.holdingId, {
        status: 'HISTORY_INCOMPLETE',
        invested: null,
        income: money(income),
        realisedPnl: null,
        totalReturn: null,
        totalReturnPct: null,
        unexplainedUnits: (openingUnits.get(holding.symbol) ?? ZERO).toString(),
      });
      continue;
    }
    const totalReturn = new Decimal(holding.value)
      .plus(totals.proceeds)
      .plus(income)
      .minus(totals.invested);
    holdings.set(holding.holdingId, {
      status: 'RECONCILED',
      invested: money(totals.invested),
      income: money(income),
      realisedPnl: money(totals.proceeds.minus(totals.invested.minus(holding.cost))),
      totalReturn: money(totalReturn),
      totalReturnPct: totals.invested.gt(0) ? pct(totalReturn.div(totals.invested)) : null,
      unexplainedUnits: null,
    });
  }

  let closedRealised = ZERO;
  let closedCount = 0;
  let closedExcluded = 0;
  for (const [symbol, totals] of symbolTrades) {
    if (currentUnits.has(symbol)) continue;
    if (!reconciled(symbol)) {
      closedExcluded += 1;
      continue;
    }
    closedCount += 1;
    closedRealised = closedRealised
      .plus(totals.proceeds)
      .plus(incomeBySymbol.get(symbol) ?? ZERO)
      .minus(totals.invested);
  }

  const breakdown: InvestmentReturnsBreakdown = {
    ...emptyBreakdown(),
    contributions: money(byCategory('CONTRIBUTION')),
    withdrawals: money(byCategory('WITHDRAWAL')),
    dividends: money(byCategory('DIVIDEND')),
    incomeTransfers: money(byCategory('INCOME_TRANSFER')),
    interest: money(byCategory('INTEREST')),
    fees: money(byCategory('FEE')),
    corporateActions: money(byCategory('CORPORATE_ACTION')),
    otherIncome: money(byCategory('OTHER_INCOME')),
    unclassified: money(byCategory('UNCLASSIFIED')),
    closedPositionsRealised: money(closedRealised),
    closedPositionsCount: closedCount,
    closedPositionsExcludedCount: closedExcluded,
  };

  const dates = [...trades.map((trade) => trade.date), ...events.map((event) => event.date)].sort();
  const historyStart = dates[0] ?? null;
  if (!historyStart) {
    return {
      account: {
        ...base,
        status: 'UNAVAILABLE',
        historyStart: null,
        valuationDate,
        currentValue: money(latest.value),
        netContributions: null,
        gain: null,
        gainPct: null,
        annualisedReturnPct: null,
        breakdown,
        issues: [
          {
            kind: 'NO_HISTORY',
            message: 'Import the HL transaction history to calculate since-inception returns.',
            amount: null,
            items: [],
          },
        ],
        periods,
        calendarYears,
        categories,
      },
      holdings,
      investorFlows: [],
      externalFlows,
      series,
    };
  }

  const recorded = sum([
    ...trades.map((trade) => new Decimal(trade.netCash)),
    ...events.map((event) => new Decimal(event.amount)),
  ]);
  const openingCash = new Decimal(latestSnapshot.cash).minus(recorded);
  const issues: InvestmentReturnsIssue[] = [];
  const includeOpeningCash = openingCash.gt(CASH_TOLERANCE);
  if (includeOpeningCash) {
    issues.push({
      kind: 'OPENING_CASH',
      message: `Some of today's cash isn't explained by the imported transactions. It is treated as money already in the account when the history begins on ${historyStart}. If it was deposited later, the real return is higher.`,
      amount: money(openingCash),
      items: [],
    });
  } else if (openingCash.lt(CASH_TOLERANCE.neg())) {
    issues.push({
      kind: 'NEGATIVE_OPENING_CASH',
      message: `The imported transactions add more cash than the account holds, so some may be duplicated or a withdrawal may be missing. The return may be understated.`,
      amount: money(openingCash),
      items: [],
    });
  }
  const unexplained = [...openingUnits]
    .filter(([, units]) => units.abs().gt(UNITS_TOLERANCE))
    .sort(([left], [right]) => left.localeCompare(right));
  if (unexplained.length > 0) {
    issues.push({
      kind: 'OPENING_POSITIONS',
      message: `${unexplained.length} positions aren't fully explained by the imported trades. They were held before the history begins, or changed by corporate actions such as mergers, consolidations or takeovers. Holdings held before the history begins aren't counted as contributions, so the gain may be overstated.`,
      amount: null,
      items: unexplained.map(([symbol, units]) => `${symbol}: ${formatUnits(units)}`),
    });
  }
  const unclassified = events.filter((event) => event.category === 'UNCLASSIFIED');
  if (unclassified.length > 0) {
    issues.push({
      kind: 'UNCLASSIFIED_CASH',
      message: `${unclassified.length} cash movements couldn't be classified. They aren't treated as contributions or withdrawals.`,
      amount: money(sum(unclassified.map((event) => new Decimal(event.amount)))),
      items: unclassified.map((event) => `${event.date} ${event.label}`),
    });
  }

  const investorFlows: DatedAmount[] = [
    ...(includeOpeningCash ? [{ date: historyStart, amount: openingCash.neg() }] : []),
    ...externalFlows.map((flow) => ({ date: flow.date, amount: flow.amount.neg() })),
  ];
  const netContributions = sum(externalFlows.map((flow) => flow.amount)).plus(
    includeOpeningCash ? openingCash : ZERO,
  );
  const gain = latest.value.minus(netContributions);
  const annualised = xirr([...investorFlows, { date: valuationDate, amount: latest.value }]);
  return {
    account: {
      ...base,
      status: issues.length === 0 ? 'COMPLETE' : 'PARTIAL',
      historyStart,
      valuationDate,
      currentValue: money(latest.value),
      netContributions: money(netContributions),
      gain: money(gain),
      gainPct: netContributions.gt(0) ? pct(gain.div(netContributions)) : null,
      annualisedReturnPct: pct(annualised),
      breakdown: { ...breakdown, openingCash: includeOpeningCash ? money(openingCash) : null },
      issues,
      periods,
      calendarYears,
      categories,
    },
    holdings,
    investorFlows,
    externalFlows,
    series,
  };
}

function combinedPeriods(results: readonly AccountReturnsResult[]): InvestmentPeriodReturn[] {
  return PERIODS.map((period) => {
    const parts = results.map((result) => ({
      result,
      period: result.account.periods.find((row) => row.period === period),
    }));
    const missing = parts.filter((part) => !part.period?.startDate || !part.period.endDate);
    if (parts.length === 0 || missing.length > 0) {
      const shared = missing.map((part) => part.period?.unavailableReason ?? '');
      return unavailablePeriod(
        period,
        parts.length === 0
          ? 'No accounts with valuations.'
          : missing.length === parts.length && new Set(shared).size === 1 && shared[0]
            ? shared[0]
            : `Not available for every account: ${missing.map((part) => part.result.account.accountName).join(', ')}.`,
      );
    }
    let startValue = ZERO;
    let endValue = ZERO;
    const flows: DatedAmount[] = [];
    const starts: string[] = [];
    const ends: string[] = [];
    for (const part of parts) {
      const startDate = part.period?.startDate;
      const endDate = part.period?.endDate;
      if (!startDate || !endDate) continue;
      starts.push(startDate);
      ends.push(endDate);
      startValue = startValue.plus(
        part.result.series.find((point) => point.date === startDate)?.value ?? ZERO,
      );
      endValue = endValue.plus(
        part.result.series.find((point) => point.date === endDate)?.value ?? ZERO,
      );
      flows.push(...flowsBetween(part.result.externalFlows, startDate, endDate));
    }
    const startDate = starts.sort()[0] ?? '';
    const endDate = ends.sort().at(-1) ?? '';
    const result = modifiedDietz({ startValue, endValue, startDate, endDate, flows });
    if (result === null) return unavailablePeriod(period, 'Withdrawals exceed the starting value.');
    const netFlows = sum(flows.map((flow) => flow.amount));
    return {
      period,
      returnPct: pct(result),
      gain: money(endValue.minus(startValue).minus(netFlows)),
      netFlows: money(netFlows),
      startDate,
      endDate,
      unavailableReason: null,
    };
  });
}

export function combineAccountReturns(
  currency: string,
  results: readonly AccountReturnsResult[],
): InvestmentCurrencyReturns {
  const inCurrency = results.filter((result) => result.account.currency === currency);
  const included = inCurrency.filter((result) => result.account.status !== 'UNAVAILABLE');
  const excluded = inCurrency.filter((result) => result.account.status === 'UNAVAILABLE');
  const valued = inCurrency.filter((result) => result.account.valuationDate !== null);
  const issues: InvestmentReturnsIssue[] = [
    ...excluded.map((result) => ({
      kind: 'ACCOUNT_EXCLUDED' as const,
      message: `${result.account.accountName} is excluded from since-inception figures: ${result.account.issues[0]?.message ?? 'returns unavailable.'}`,
      amount: null,
      items: [],
    })),
    ...included.flatMap((result) =>
      result.account.issues.map((issue) => ({
        ...issue,
        message: `${result.account.accountName}: ${issue.message}`,
      })),
    ),
  ];
  const decimalSum = (pick: (account: InvestmentAccountReturns) => string | null) =>
    sum(
      included.flatMap((result) => {
        const value = pick(result.account);
        return value === null ? [] : [new Decimal(value)];
      }),
    );
  const breakdownKeys = [
    'contributions',
    'withdrawals',
    'dividends',
    'incomeTransfers',
    'interest',
    'fees',
    'corporateActions',
    'otherIncome',
    'unclassified',
    'closedPositionsRealised',
  ] as const;
  const breakdown = emptyBreakdown();
  for (const key of breakdownKeys)
    breakdown[key] = money(decimalSum((account) => account.breakdown[key]));
  const openingCash = included.filter((result) => result.account.breakdown.openingCash !== null);
  breakdown.openingCash =
    openingCash.length > 0 ? money(decimalSum((account) => account.breakdown.openingCash)) : null;
  breakdown.closedPositionsCount = included.reduce(
    (total, result) => total + result.account.breakdown.closedPositionsCount,
    0,
  );
  breakdown.closedPositionsExcludedCount = included.reduce(
    (total, result) => total + result.account.breakdown.closedPositionsExcludedCount,
    0,
  );
  const tracking = valued.flatMap((result) =>
    result.account.trackingSince ? [result.account.trackingSince] : [],
  );
  const base = {
    currency,
    accountIds: inCurrency.map((result) => result.account.accountId),
    breakdown,
    issues,
    periods: combinedPeriods(valued),
    trackingSince: tracking.length === valued.length ? (tracking.sort().at(-1) ?? null) : null,
    snapshotCount: valued.reduce((total, result) => total + result.account.snapshotCount, 0),
  };
  if (included.length === 0) {
    return {
      ...base,
      status: 'UNAVAILABLE',
      historyStart: null,
      valuationDate: null,
      currentValue: null,
      netContributions: null,
      gain: null,
      gainPct: null,
      annualisedReturnPct: null,
    };
  }
  const currentValue = decimalSum((account) => account.currentValue);
  const netContributions = decimalSum((account) => account.netContributions);
  const gain = currentValue.minus(netContributions);
  const annualised = xirr(
    included.flatMap((result) => [
      ...result.investorFlows,
      {
        date: result.account.valuationDate ?? '',
        amount: new Decimal(result.account.currentValue ?? 0),
      },
    ]),
  );
  return {
    ...base,
    status: issues.length === 0 ? 'COMPLETE' : 'PARTIAL',
    historyStart: included.flatMap((result) => result.account.historyStart ?? []).sort()[0] ?? null,
    valuationDate:
      included
        .flatMap((result) => result.account.valuationDate ?? [])
        .sort()
        .at(-1) ?? null,
    currentValue: money(currentValue),
    netContributions: money(netContributions),
    gain: money(gain),
    gainPct: netContributions.gt(0) ? pct(gain.div(netContributions)) : null,
    annualisedReturnPct: pct(annualised),
  };
}
